import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  MinusIcon,
  PlusIcon,
  Square3Stack3DIcon,
  ViewfinderCircleIcon,
} from '@heroicons/react/24/solid';
import MapboxLanguage from '@mapbox/mapbox-gl-language';
import dissolve from '@turf/dissolve';
import {feature, featureCollection} from '@turf/helpers';
import {bbox} from '@turf/turf';
import * as geojson from 'geojson';
import {throttle} from 'lodash';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import React from 'react';
import {Layer, Map as MapGL, MapRef, Source, useControl, useMap} from 'react-map-gl/mapbox';
import type {MapMouseEvent} from 'react-map-gl/mapbox';

import {useFeatures} from 'src/hooks/useFeatures';
import {MaybeApiFlowContributingAreas} from 'src/hooks/useFlowContributingAreas';
import {useResizeMapOnChange} from 'src/hooks/useMapResize';
import {contextOrganizationId} from 'src/lib/api/api';
import {Project, Site, SiteType} from 'src/lib/api/project';
import COLORS from 'src/lib/colors';
import {HR_FLOWLINES_FEATURE_COLLECTION_ID} from 'src/lib/constants';
import {MAPBOX_ACCESS_TOKEN, MAPBOX_STYLE_IDS, mapboxStyleUrl} from 'src/lib/map';
import {makeSiteMapFeatures} from 'src/lib/mapFeatures';

import {recordEvent} from './Analytics';
import {
  ACTIVE_POINT_LABEL_LAYOUT,
  ACTIVE_POINT_LABEL_PAINT,
  BASIN_FILL_PAINT,
  BASIN_POINT_PAINT,
  BASIN_ROUTED_POINT_PAINT,
  BASIN_SITE_TYPES_FILTER,
  BASIN_STROKE_DESELECTED_PAINT,
  BASIN_STROKE_SELECTED_PAINT,
  CONTRIBUTING_AREA_EDGE_STROKE_PAINT,
  CONTRIBUTING_AREA_STROKE_PAINT,
  FLOWLINES_PAINT,
  GAUGE_POINT_PAINT,
  LOCATION_POINT_PAINT,
} from './geoVizLayerStyles';
import {
  ACTIVE_POINT_LABEL_ID,
  BASIN_FILL_LAYER_ID,
  BASIN_POINT_CIRCLE_LAYER_ID,
  BASIN_ROUTED_POINT_CIRCLE_LAYER_ID,
  BASIN_STROKE_DESELECTED_LAYER_ID,
  BASIN_STROKE_SELECTED_LAYER_ID,
  CONTRIBUTING_AREA_EDGE_SOURCE_ID,
  CONTRIBUTING_AREA_EDGE_STROKE_LAYER_ID,
  CONTRIBUTING_AREA_STROKE_LAYER_ID,
  DRAINAGE_SOURCE_ID,
  FLOWLINES_LAYER_ID,
  FLOWLINES_SOURCE_ID,
  FORECAST_POINT_SOURCE_ID,
  GAUGE_POINT_CIRCLE_LAYER_ID,
  HOVERED_FEATURE_STATE_KEY,
  LOCATION_POINT_CIRCLE_LAYER_ID,
  SELECTED_CONTRIBUTING_AREA_STATE_KEY,
  SELECTED_SITE_STATE_KEY,
} from './geoVizMapIds';
import {useTranslations} from './TranslationsContext';
import VizLoading from './Viz/VizLoading';

type SiteId = Site['id'];

type Props = {
  firebaseToken: string | null;
  dims: [number, number];
  project: Project;
  /*
  Render even if flow contributing areas have not loaded. The requests can be
  slow and we don’t want them to hold up the dashboard. The map will eventually
  update when the requests resolve.
  */
  flowContributingAreas: MaybeApiFlowContributingAreas;
  selectedSite: Site;
  setSelectedSiteId: (siteId: SiteId) => void;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
};

type BasemapId = 'vector' | 'terrain' | 'satellite';

const BASEMAP_STYLES: {
  id: BasemapId;
  labelKey: 'basemapVector' | 'basemapTerrain' | 'basemapSatellite';
  url: string;
}[] = [
  {id: 'vector', labelKey: 'basemapVector', url: mapboxStyleUrl(MAPBOX_STYLE_IDS.light)},
  {id: 'terrain', labelKey: 'basemapTerrain', url: mapboxStyleUrl(MAPBOX_STYLE_IDS.outdoors)},
  {
    id: 'satellite',
    labelKey: 'basemapSatellite',
    url: mapboxStyleUrl(MAPBOX_STYLE_IDS.satelliteStreets),
  },
];

type BBox2d = [number, number, number, number];

type ContributingAreaEdge = geojson.FeatureCollection;
type ContributingAreaEdgeBySiteId = {[siteId: SiteId]: ContributingAreaEdge};

const FLOWLINES_SOURCE_LAYER = 'NetworkNHDFlowline';

const MARGIN = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
};
const GEO_FIT_PADDING = 10;

// Layers whose features resolve to a site on click/hover.
const EVENT_LAYERS = [
  BASIN_FILL_LAYER_ID,
  BASIN_POINT_CIRCLE_LAYER_ID,
  BASIN_ROUTED_POINT_CIRCLE_LAYER_ID,
  LOCATION_POINT_CIRCLE_LAYER_ID,
  GAUGE_POINT_CIRCLE_LAYER_ID,
];

/*
Hit-testing is done here, in the handlers, rather than through
interactiveLayerIds: with that prop react-map-gl queries on every raw
mousemove, before the throttle can drop the event. The layers can be briefly
absent mid basemap swap, so query only the ones present.
*/
function queryEventFeatures(m: mapboxgl.Map, e: MapMouseEvent): mapboxgl.MapboxGeoJSONFeature[] {
  return m.queryRenderedFeatures(e.point, {
    layers: EVENT_LAYERS.filter((id) => m.getLayer(id)),
  });
}

const EMPTY_FEATURE_COLLECTION = featureCollection([]);

/*
MapboxLanguage localizes basemap labels. Mounted as a react-map-gl control so
it attaches/detaches with the map; on locale change it rewrites the current
style's text fields in place.
*/
function LanguageControl({locale}: {locale: string}) {
  const language = useControl(() => new MapboxLanguage({defaultLanguage: locale}));

  const appliedLocale = React.useRef(locale);
  // useControl gives the control instance but not the map; react-map-gl
  // exposes the map to children through context via useMap.
  const map = useMap().current?.getMap() as mapboxgl.Map | undefined;

  React.useEffect(() => {
    if (!map || appliedLocale.current === locale) {
      return;
    }
    appliedLocale.current = locale;
    map.setStyle(language.setLanguage(map.getStyle(), locale));
  }, [map, language, locale]);

  return null;
}

export default function GeoViz({
  firebaseToken,
  dims,
  project,
  flowContributingAreas,
  selectedSite,
  setSelectedSiteId,
  expanded,
  setExpanded,
}: Props) {
  const {t, locale} = useTranslations();
  const [basemapId, setBasemapId] = React.useState<BasemapId>('vector');
  const [basemapMenuOpen, setBasemapMenuOpen] = React.useState<boolean>(false);
  const basemapMenuRef = React.useRef<HTMLDivElement>(null);

  // Dismiss the basemap flyout when a click lands anywhere outside it —
  // without this the menu stays open until the button is clicked again.
  React.useEffect(() => {
    if (!basemapMenuOpen) {
      return;
    }
    const handleMousedown = (e: MouseEvent) => {
      if (basemapMenuRef.current && !basemapMenuRef.current.contains(e.target as Node)) {
        setBasemapMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMousedown);
    return () => document.removeEventListener('mousedown', handleMousedown);
  }, [basemapMenuOpen]);

  const drainageFeaturesQuery = useFeatures(
    project.sites?.flatMap((s) => s.featureIds.drainageAreaFeatureId ?? []) || []
  );
  const forecastPointFeaturesQuery = useFeatures(
    project.sites?.flatMap((s) => s.featureIds.forecastPointFeatureId ?? []) || []
  );

  const mapRef = React.useRef<MapRef>(null);
  const [mapLoaded, setMapLoaded] = React.useState(false);
  const [cursor, setCursor] = React.useState<string>('auto');

  /*
  react-map-gl re-adds the declarative <Source>/<Layer> children after a
  basemap swap, but feature state (selection, hover) is not declarative and is
  wiped with the old style. Bumped once the replacement style has our sources
  again, so the selection effect re-applies against them.
  */
  const [styleVersion, setStyleVersion] = React.useState(0);

  const showFlowlinesByDefault = locale === 'en';
  const [showFlowlines, setShowFlowlines] = React.useState<boolean>(showFlowlinesByDefault);

  const [bounds, drainageFc, forecastPointFc, siteFeatures] = React.useMemo(() => {
    if (!drainageFeaturesQuery.data || !forecastPointFeaturesQuery.data || !project.sites) {
      return [];
    }

    const siteFeatures = makeSiteMapFeatures(
      project.sites,
      drainageFeaturesQuery.data,
      forecastPointFeaturesQuery.data
    );
    const drainageFc = featureCollection(siteFeatures.drainage.features);
    const forecastPointFc = featureCollection(siteFeatures.forecastPoints.features);
    const bounds = bbox(
      featureCollection([...drainageFc.features, ...forecastPointFc.features])
    ) as BBox2d;
    return [bounds, drainageFc, forecastPointFc, siteFeatures];
  }, [drainageFeaturesQuery.data, forecastPointFeaturesQuery.data, project.sites]);

  /*
  For every site ID in the flow contributing areas object, isolate the Features
  from the drainage FeatureCollection and dissolve them into a single Polygon
  feature, which should contain an outer ring containing all dissolved Features.
  */
  const contributingAreaEdgeBySiteId = React.useMemo<ContributingAreaEdgeBySiteId>(() => {
    if (!flowContributingAreas || !drainageFc) {
      return {};
    }

    const siteIds = Object.keys(flowContributingAreas);

    return siteIds.reduce<ContributingAreaEdgeBySiteId>((contributingAreaEdgeBySiteId, siteId) => {
      const contributingAreas = flowContributingAreas[siteId];

      /*
          This is unlikely, but if there aren’t any contributing areas for the
          site ID on the flow contributing areas object, then don’t set
          anything. We handle undefined contributing area edges when choosing
          the source data below.
          */
      if (!contributingAreas || !contributingAreas.length) {
        return contributingAreaEdgeBySiteId;
      }

      const contributingAreaFeatureIds = contributingAreas.map(({featureId}) =>
        featureId.toString()
      );

      const contributingAreaFeatures = drainageFc.features.filter(
        ({id}) => id !== undefined && contributingAreaFeatureIds.includes(id.toString())
      );

      const contributingAreaFeatureCollection = featureCollection(contributingAreaFeatures);

      /*
          Turf.js dissolve only accepts Features with Polygon geometries. While
          I think it’s generally safe to assume that drainage Features will have
          Polygon geometries, as they represent a physical area over which water
          flows and are unlikely to be discontiguous or one-dimensional, we
          account for all possible geometry types.
          */
      const contributingAreaPolygonFeatures = contributingAreaFeatureCollection.features
        .map((contributingAreaFeature) => {
          switch (contributingAreaFeature.geometry.type) {
            case 'Polygon':
              return contributingAreaFeature;

            case 'MultiPolygon':
              return contributingAreaFeature.geometry.coordinates.map((coordinates) => {
                return feature({type: 'Polygon', coordinates});
              });

            default:
              return null;
          }
        })
        .flat()
        .filter((x): x is geojson.Feature<geojson.Polygon> => !!x);

      let contributingAreaDissolved: geojson.FeatureCollection<geojson.Polygon>;
      try {
        contributingAreaDissolved = dissolve(featureCollection(contributingAreaPolygonFeatures));
      } catch {
        contributingAreaDissolved = featureCollection(contributingAreaPolygonFeatures);
      }

      /*
          The dissolved FeatureCollection Features sometimes contain inner rings
          where coordinates from adjacent Features did not line up exactly. Per
          the GeoJSON specification, the outer ring should always be first in
          the list of coordinates, so we drop all elements after it to ignore
          any extra inner rings.
          */
      const contributingAreaEdge = featureCollection(
        contributingAreaDissolved.features.map((feature) => ({
          ...feature,
          geometry: {
            ...feature.geometry,
            coordinates: [feature.geometry.coordinates[0]],
          },
        }))
      );

      contributingAreaEdgeBySiteId[siteId] = contributingAreaEdge;
      return contributingAreaEdgeBySiteId;
    }, {});
  }, [flowContributingAreas, drainageFc]);

  const transformRequest = React.useCallback(
    (url: string) => ({url: formatTileUrl(url, firebaseToken)}),
    [firebaseToken]
  );

  const basemapUrl = BASEMAP_STYLES.find((b) => b.id === basemapId)!.url;

  /*
  The contributingAreaEdge may be undefined if a selected site does not have
  contributing areas (e.g., a sub-basin).
  */
  const contributingAreaEdge: ContributingAreaEdge =
    contributingAreaEdgeBySiteId[selectedSite.id] || EMPTY_FEATURE_COLLECTION;

  const map = mapLoaded ? mapRef.current?.getMap() : undefined;

  /*
  Highlight the selected site (and its contributing areas) by setting mapbox
  feature state. Sources and layers render declaratively above, but feature
  state has no component form, so it's applied here whenever the selection
  changes — and re-applied after a basemap swap (styleVersion), which clears it.
  */
  React.useEffect(() => {
    // A basemap swap tears down map.style before the replacement style loads;
    // getSource() derefs it unconditionally and throws mid-swap.
    if (!map || !map.isStyleLoaded() || !map.getSource(DRAINAGE_SOURCE_ID)) {
      return;
    }

    // remove feature state so previous is not selected
    map.removeFeatureState({source: DRAINAGE_SOURCE_ID});
    map.removeFeatureState({source: CONTRIBUTING_AREA_EDGE_SOURCE_ID});
    map.removeFeatureState({source: FORECAST_POINT_SOURCE_ID});

    const drainageAreaFeatureId = siteFeatures?.drainage.featureIdBySiteId[selectedSite.id];
    if (drainageAreaFeatureId) {
      map.setFeatureState(
        {
          source: DRAINAGE_SOURCE_ID,
          id: drainageAreaFeatureId,
        },
        {
          [SELECTED_SITE_STATE_KEY]: true,
          [SELECTED_CONTRIBUTING_AREA_STATE_KEY]: false,
        }
      );
    }

    const forecastPointFeatureId = siteFeatures?.forecastPoints.featureIdBySiteId[selectedSite.id];
    if (forecastPointFeatureId) {
      map.setFeatureState(
        {
          source: FORECAST_POINT_SOURCE_ID,
          id: forecastPointFeatureId,
        },
        {[SELECTED_SITE_STATE_KEY]: true}
      );
    }

    if (flowContributingAreas?.[selectedSite.id]) {
      flowContributingAreas[selectedSite.id].forEach(({featureId}) => {
        map.setFeatureState(
          {
            source: DRAINAGE_SOURCE_ID,
            id: featureId,
          },
          {
            [SELECTED_SITE_STATE_KEY]: true,
            [SELECTED_CONTRIBUTING_AREA_STATE_KEY]: true,
          }
        );
      });
    }
  }, [map, flowContributingAreas, selectedSite, siteFeatures, styleVersion]);

  const hoveredFeatureRef = React.useRef<mapboxgl.MapboxGeoJSONFeature | null>(null);

  const handleMouseMove = React.useMemo(
    () =>
      throttle((e: MapMouseEvent) => {
        const m = mapRef.current?.getMap();
        if (!m) {
          return;
        }
        const [firstFeature] = queryEventFeatures(m, e);
        const hoveredFeature = hoveredFeatureRef.current;
        if (firstFeature) {
          if (hoveredFeature && firstFeature.id !== hoveredFeature.id) {
            m.setFeatureState(
              {source: hoveredFeature.source, id: hoveredFeature.id},
              {[HOVERED_FEATURE_STATE_KEY]: false}
            );
          }

          m.setFeatureState(
            {source: firstFeature.source, id: firstFeature.id},
            {[HOVERED_FEATURE_STATE_KEY]: true}
          );
          hoveredFeatureRef.current = firstFeature as mapboxgl.MapboxGeoJSONFeature;
          setCursor('pointer');
        } else {
          if (hoveredFeature) {
            m.setFeatureState(
              {source: hoveredFeature.source, id: hoveredFeature.id},
              {[HOVERED_FEATURE_STATE_KEY]: false}
            );
            hoveredFeatureRef.current = null;
          }
          setCursor('auto');
        }
      }, 50),
    []
  );

  const handleClick = React.useCallback(
    (e: MapMouseEvent) => {
      const m = mapRef.current?.getMap();
      if (!m) {
        return;
      }
      const [firstFeature] = queryEventFeatures(m, e);
      if (!firstFeature) {
        return;
      }
      // every feature in both sources carries the siteId property (see mapFeatures.ts)
      const nextSelectedSite = project.sites?.find((s) => s.id === firstFeature.properties?.siteId);
      if (nextSelectedSite) {
        setSelectedSiteId(nextSelectedSite.id);
        recordEvent('site_selected', {
          siteId: nextSelectedSite.id,
          siteName: nextSelectedSite.name,
          projectId: project.id,
          method: 'map_click',
        });
      }
    },
    [project, setSelectedSiteId]
  );

  /*
  A basemap swap replaces the style; react-map-gl re-adds the declarative
  children on its own, but the wiped feature state has to be re-applied by us.
  Verified at idle — the first point after a swap where the re-added sources
  are reliably queryable — by checking whether the selected feature still
  carries its state; bumping styleVersion re-fires the selection effect.
  */
  const handleIdle = React.useCallback(() => {
    const m = mapRef.current?.getMap();
    if (!m) {
      return;
    }

    const isSelected = (source: string, id: string | number) =>
      !!m.getSource(source) && m.getFeatureState({source, id})[SELECTED_SITE_STATE_KEY] === true;

    const drainageFeatureId = siteFeatures?.drainage.featureIdBySiteId[selectedSite.id];
    const forecastPointFeatureId = siteFeatures?.forecastPoints.featureIdBySiteId[selectedSite.id];

    const selectionApplied = drainageFeatureId
      ? isSelected(DRAINAGE_SOURCE_ID, drainageFeatureId)
      : forecastPointFeatureId
        ? isSelected(FORECAST_POINT_SOURCE_ID, forecastPointFeatureId)
        : true;

    if (!selectionApplied) {
      setStyleVersion((v) => v + 1);
    }
  }, [siteFeatures, selectedSite]);

  // dims changes come from the expand toggle and page layout
  useResizeMapOnChange(mapRef, [...dims]);

  const mapWidth = dims[0] - MARGIN.left - MARGIN.right;
  const mapHeight = dims[1] - MARGIN.top - MARGIN.bottom;

  return (
    <div className="relative">
      {drainageFeaturesQuery.isFetched && forecastPointFeaturesQuery.isFetched && bounds ? (
        <div
          style={{
            width: mapWidth,
            height: mapHeight,
            marginLeft: MARGIN.left,
            marginTop: MARGIN.top,
            marginRight: MARGIN.right,
            marginBottom: MARGIN.bottom,
          }}
        >
          <MapGL
            ref={mapRef}
            mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
            mapStyle={basemapUrl}
            initialViewState={{bounds, fitBoundsOptions: {padding: GEO_FIT_PADDING}}}
            transformRequest={transformRequest}
            cursor={cursor}
            onClick={handleClick}
            onMouseMove={handleMouseMove}
            onLoad={() => setMapLoaded(true)}
            onIdle={handleIdle}
            style={{width: '100%', height: '100%'}}
          >
            <LanguageControl locale={locale} />

            <Source
              id={FLOWLINES_SOURCE_ID}
              type="vector"
              tiles={[
                'https://api.upstream.tech/tiles/' +
                  HR_FLOWLINES_FEATURE_COLLECTION_ID +
                  '/{z}/{x}/{y}.pbf',
              ]}
            >
              <Layer
                id={FLOWLINES_LAYER_ID}
                source-layer={FLOWLINES_SOURCE_LAYER}
                type="line"
                layout={{visibility: showFlowlines ? 'visible' : 'none'}}
                paint={FLOWLINES_PAINT}
              />
            </Source>

            <Source id={DRAINAGE_SOURCE_ID} type="geojson" data={drainageFc}>
              {/* fills for siteType BASIN */}
              <Layer
                id={BASIN_FILL_LAYER_ID}
                type="fill"
                paint={BASIN_FILL_PAINT}
                filter={BASIN_SITE_TYPES_FILTER}
              />
              {/* stroke for deselected siteType BASIN */}
              <Layer
                id={BASIN_STROKE_DESELECTED_LAYER_ID}
                type="line"
                paint={BASIN_STROKE_DESELECTED_PAINT}
                filter={BASIN_SITE_TYPES_FILTER}
              />
              {/* stroke for selected siteType BASIN */}
              <Layer
                id={BASIN_STROKE_SELECTED_LAYER_ID}
                type="line"
                paint={BASIN_STROKE_SELECTED_PAINT}
                filter={BASIN_SITE_TYPES_FILTER}
              />
              {/* stroke for contributing area */}
              <Layer
                id={CONTRIBUTING_AREA_STROKE_LAYER_ID}
                type="line"
                paint={CONTRIBUTING_AREA_STROKE_PAINT}
                filter={BASIN_SITE_TYPES_FILTER}
              />
            </Source>

            <Source
              id={CONTRIBUTING_AREA_EDGE_SOURCE_ID}
              type="geojson"
              data={contributingAreaEdge}
            >
              {/* stroke for contributing area edge */}
              <Layer
                id={CONTRIBUTING_AREA_EDGE_STROKE_LAYER_ID}
                type="line"
                paint={CONTRIBUTING_AREA_EDGE_STROKE_PAINT}
              />
            </Source>

            <Source id={FORECAST_POINT_SOURCE_ID} type="geojson" data={forecastPointFc}>
              {/* point for siteType BASIN */}
              <Layer
                id={BASIN_POINT_CIRCLE_LAYER_ID}
                type="circle"
                paint={BASIN_POINT_PAINT}
                filter={['==', ['get', 'siteType'], SiteType.BASIN]}
              />
              {/* point for siteType BASIN ROUTED */}
              <Layer
                id={BASIN_ROUTED_POINT_CIRCLE_LAYER_ID}
                type="circle"
                paint={BASIN_ROUTED_POINT_PAINT}
                filter={['==', ['get', 'siteType'], SiteType.BASIN_ROUTED]}
              />
              {/* point for siteType LOCATION */}
              <Layer
                id={LOCATION_POINT_CIRCLE_LAYER_ID}
                type="circle"
                paint={LOCATION_POINT_PAINT}
                filter={['==', ['get', 'siteType'], SiteType.LOCATION]}
              />
              {/* point for siteType GAUGE */}
              <Layer
                id={GAUGE_POINT_CIRCLE_LAYER_ID}
                type="circle"
                paint={GAUGE_POINT_PAINT}
                filter={['==', ['get', 'siteType'], SiteType.GAUGE]}
              />
              <Layer
                id={ACTIVE_POINT_LABEL_ID}
                type="symbol"
                layout={ACTIVE_POINT_LABEL_LAYOUT}
                paint={ACTIVE_POINT_LABEL_PAINT}
              />
            </Source>
          </MapGL>
        </div>
      ) : (
        <VizLoading dims={dims} margin={MARGIN} type="bg" />
      )}
      {/*
      Overlay controls, grouped by what they act on: viewport (zoom, fit),
      content (basemap, flowlines), then layout (expand).
      */}
      <div className="absolute right-2 top-2 flex flex-col gap-y-2">
        <div className={CONTROL_GROUP_CLASS}>
          <MapControlButton label={t('zoomIn')} onClick={() => mapRef.current?.zoomIn()}>
            <PlusIcon className={CONTROL_ICON_CLASS} />
          </MapControlButton>
          <MapControlButton label={t('zoomOut')} onClick={() => mapRef.current?.zoomOut()}>
            <MinusIcon className={CONTROL_ICON_CLASS} />
          </MapControlButton>
          <MapControlButton
            label={t('fitToBounds')}
            onClick={() => {
              if (mapRef.current && bounds) {
                mapRef.current.fitBounds(bounds, {padding: GEO_FIT_PADDING});
              }
            }}
          >
            {/*
            A viewfinder rather than the arrows-pointing-in glyph this used to
            carry: the expand toggle below collapses with arrows-pointing-in,
            and two buttons reading identically is worse than moving this one to
            a mark that says "recenter" on its own.
            */}
            <ViewfinderCircleIcon className={CONTROL_ICON_CLASS} />
          </MapControlButton>
        </div>

        {/*
        The basemap flyout lives outside the group card so the card can't clip
        it; clicks on clipped content fall through to the map canvas.
        */}
        <div className="relative" ref={basemapMenuRef}>
          <div className={CONTROL_GROUP_CLASS}>
            <MapControlButton
              label={t('basemap')}
              onClick={() => setBasemapMenuOpen((v) => !v)}
              suppressTooltip={basemapMenuOpen}
            >
              <Square3Stack3DIcon className={CONTROL_ICON_CLASS} />
            </MapControlButton>
            {firebaseToken && (
              <MapControlButton
                label={t(showFlowlines ? 'hideFlowlines' : 'showFlowlines')}
                tooltipExtra={t('flowlinesAvailability')}
                /*
                Even though bg-teal-50 is in the documentation, it’s not
                available in the TailwindCSS package. (Maybe it was exported in
                a more recent version.) So, we update the background color
                directly via a style attribute.
                */
                style={showFlowlines ? {backgroundColor: COLORS.FLOWLINE_BUTTON_BG} : undefined}
                onClick={() => {
                  setShowFlowlines((prevShowFlowlines) => !prevShowFlowlines);
                }}
              >
                <RiversIcon
                  className={CONTROL_ICON_CLASS}
                  stroke={showFlowlines ? COLORS.FLOWLINE_MAX : undefined}
                />
              </MapControlButton>
            )}
          </div>
          {basemapMenuOpen && (
            <div className="absolute top-0 right-10 z-10 bg-white rounded shadow p-1 text-xs flex flex-col min-w-[7rem]">
              {BASEMAP_STYLES.map((b) => (
                <button
                  key={b.id}
                  className={`text-left px-2 py-1 rounded ${
                    basemapId === b.id ? 'bg-gray-200' : 'hover:bg-gray-100'
                  }`}
                  onClick={() => {
                    setBasemapId(b.id);
                    setBasemapMenuOpen(false);
                  }}
                >
                  {t(b.labelKey)}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className={CONTROL_GROUP_CLASS}>
          <MapControlButton
            label={expanded ? t('collapseMap') : t('expandMap')}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <ArrowsPointingInIcon className={CONTROL_ICON_CLASS} />
            ) : (
              <ArrowsPointingOutIcon className={CONTROL_ICON_CLASS} />
            )}
          </MapControlButton>
        </div>
      </div>
    </div>
  );
}

/*
One rounded card per cluster; divide-y draws the separators between buttons.
The card must NOT clip (no overflow-hidden) — the buttons' hover tooltips
extend past its left edge — so the end buttons carry the card's corner
rounding themselves.
*/
const CONTROL_GROUP_CLASS = 'flex flex-col rounded bg-gray-50 shadow divide-y divide-gray-200';
const CONTROL_ICON_CLASS = 'w-4 h-4';

/*
The tooltip is custom rather than a native title attribute so every control
shows the same style with the same timing; label doubles as the accessible
name. suppressTooltip hides it while a button's flyout is open, since the two
would overlap.
*/
const MapControlButton: React.FunctionComponent<{
  label: string;
  onClick: () => void;
  style?: React.CSSProperties;
  tooltipExtra?: string;
  suppressTooltip?: boolean;
  children: React.ReactNode;
}> = ({label, onClick, style, tooltipExtra, suppressTooltip, children}) => (
  <button
    className="group/control relative w-8 h-8 flex items-center justify-center bg-gray-50 hover:bg-gray-100 first:rounded-t last:rounded-b"
    aria-label={label}
    style={style}
    onClick={onClick}
  >
    {children}
    {!suppressTooltip && (
      <span
        role="tooltip"
        className={`invisible group-hover/control:visible absolute right-full mr-2 top-1/2 -translate-y-1/2 z-10 pointer-events-none rounded bg-gray-900 text-white text-xs px-2 py-1 text-left font-normal ${
          tooltipExtra ? 'w-52' : 'whitespace-nowrap'
        }`}
      >
        <b>{label}</b>
        {tooltipExtra && <span className="block mt-1 text-gray-300">{tooltipExtra}</span>}
      </span>
    )}
  </button>
);

const RiversIcon: React.FunctionComponent<{
  className?: string;
  stroke?: string;
}> = ({className, stroke}) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M4 7C4 7 6.33473 5 8.125 5C9.91527 5 12.25 7 12.25 7C12.25 7 14.5847 9 16.375 9C18.1653 9 20.5 7 20.5 7M4 12.25C4 12.25 6.24181 10.2977 7.981 10.25C9.82134 10.1995 12.25 12.25 12.25 12.25C12.25 12.25 14.5847 14.25 16.375 14.25C18.1653 14.25 20.5 12.25 20.5 12.25M4 17.5C4 17.5 6.2696 15.8133 7.981 15.5C11.5529 14.846 12.7932 20.0971 16.375 19.5C18.1409 19.2056 20.5 17.5 20.5 17.5"
      stroke={stroke || '#0F172A'}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function formatTileUrl(
  url: string,
  firebaseToken: string | null,
  appendApiToken: boolean = true
): string {
  /*
  Requests to a local server or one of our production domains should get the
  Firebase auth token added on to authenticate the request.
  */
  if (isTilerUrl(url)) {
    const parsedUrl = new URL(url);

    if (contextOrganizationId) {
      parsedUrl.searchParams.set('cOrgId', contextOrganizationId);
    }

    if (appendApiToken && firebaseToken) {
      /*
      Send the token as a query param to avoid needing to make a preflight
      request first (custom headers, including `Authorization`, require a CORS
      preflight request).
      */
      parsedUrl.searchParams.set('apiToken', firebaseToken);
    }

    url = decodeURI(parsedUrl.toString());
  }

  return url;
}

function isTilerUrl(url: string): boolean {
  return (
    url.startsWith('http://localhost') || !!url.match(/^https?:\/\/([a-zA-Z]+\.)*upstream\.tech\//)
  );
}
